import * as React from "react"
import { useApplication, useCulture } from "@florisoft/framework"

import { cn } from "../../lib/utils"
import { useUiTranslation } from "../../localization/use-ui-translation"
import { Label } from "./label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"

export type ThemePreference = "light" | "dark" | "system"

interface ThemeContextValue {
  theme: ThemePreference
  setTheme: (theme: ThemePreference) => void
}

interface ThemeProviderProps {
  children: React.ReactNode
  /**
   * The `localStorage` key that remembers the chosen theme.
   * @default "florisoft.ui.theme"
   */
  storageKey?: string
}

interface LanguageSwitcherProps {
  /**
   * The cultures the user can pick, such as `["nl-NL", "en-US"]`. The current UI culture is matched against them
   * (exactly, then by language, then the first one is used). Labels come from the UI localization for English and
   * Dutch and otherwise from the browser's language names.
   */
  cultures: readonly string[]
  className?: string
}

interface PreferenceControlsProps extends LanguageSwitcherProps {
  /** Class name for the theme control. */
  themeClassName?: string
  /** Class name for the language control. */
  languageClassName?: string
}

const defaultThemeStorageKey = "florisoft.ui.theme"
const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined)

function isThemePreference(value: string | null): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system"
}

function readThemePreference(storageKey: string): ThemePreference {
  if (typeof window === "undefined") return "system"
  try {
    const stored = window.localStorage.getItem(storageKey)
    return isThemePreference(stored) ? stored : "system"
  } catch {
    return "system"
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  )
}

/**
 * Holds the user's theme preference (`light`, `dark` or `system`), remembers it in `localStorage` and puts the `dark`
 * class and `color-scheme` on the document element. With `system` it follows the operating system and updates when
 * that changes. Wrap the app in it once; `useTheme` and `ThemeSwitcher` need it.
 */
export function ThemeProvider({ children, storageKey = defaultThemeStorageKey }: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<ThemePreference>(() => readThemePreference(storageKey))

  React.useEffect(() => {
    const applyTheme = () => {
      const isDark = theme === "dark" || (theme === "system" && systemPrefersDark())
      document.documentElement.classList.toggle("dark", isDark)
      document.documentElement.style.colorScheme = isDark ? "dark" : "light"
    }

    applyTheme()
    if (theme !== "system" || typeof window.matchMedia !== "function") return

    const media = window.matchMedia("(prefers-color-scheme: dark)")
    media.addEventListener("change", applyTheme)
    return () => media.removeEventListener("change", applyTheme)
  }, [theme])

  const value = React.useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme: (nextTheme) => {
        try {
          window.localStorage.setItem(storageKey, nextTheme)
        } catch {
          // Theme changes remain usable when browser persistence is unavailable.
        }
        setThemeState(nextTheme)
      },
    }),
    [storageKey, theme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/** The theme preference and a setter for it, from the enclosing ThemeProvider. Throws outside one. */
export function useTheme() {
  const context = React.useContext(ThemeContext)
  if (!context) throw new Error("useTheme must be used within ThemeProvider")
  return context
}

/** A labelled select to choose between the system, light and dark theme. Must be inside a ThemeProvider. */
export function ThemeSwitcher({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme()
  const { t } = useUiTranslation()
  const selectId = React.useId()
  const items = [
    { value: "system", label: t(($) => $.theme.system) },
    { value: "light", label: t(($) => $.theme.light) },
    { value: "dark", label: t(($) => $.theme.dark) },
  ]

  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      <Label htmlFor={selectId} className="text-xs text-muted-foreground">
        {t(($) => $.theme.label)}
      </Label>
      <Select
        items={items}
        value={theme}
        onValueChange={(value) => {
          if (isThemePreference(value)) setTheme(value)
        }}
      >
        <SelectTrigger id={selectId} className="min-w-32">
          <SelectValue placeholder={t(($) => $.theme.choose)} />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function matchCulture(uiCulture: string, cultures: readonly string[]) {
  const normalized = uiCulture.toLowerCase()
  return (
    cultures.find((culture) => culture.toLowerCase() === normalized) ??
    cultures.find((culture) => culture.toLowerCase().split("-")[0] === normalized.split("-")[0]) ??
    cultures[0]
  )
}

function getCultureLabel(culture: string, uiCulture: string, knownLabels: Readonly<Record<string, string>>) {
  const language = culture.toLowerCase().split("-")[0]
  const knownLabel = knownLabels[language]
  if (knownLabel) return knownLabel
  try {
    return new Intl.DisplayNames([uiCulture], { type: "language" }).of(culture) ?? culture
  } catch {
    return culture
  }
}

/**
 * A labelled select to change the application's UI culture. Choosing one calls the application's localization to
 * switch culture, and the select is disabled until that finishes; a message shows if it fails.
 */
export function LanguageSwitcher({ cultures, className }: LanguageSwitcherProps) {
  const application = useApplication()
  const { uiCulture } = useCulture()
  const { t } = useUiTranslation()
  const selectId = React.useId()
  const [changing, setChanging] = React.useState(false)
  const [error, setError] = React.useState(false)
  const selectedCulture = matchCulture(uiCulture, cultures)
  const knownLabels = React.useMemo(
    () => ({
      en: t(($) => $.language.english),
      nl: t(($) => $.language.dutch),
    }),
    [t]
  )
  const items = React.useMemo(
    () => cultures.map((value) => ({ value, label: getCultureLabel(value, uiCulture, knownLabels) })),
    [cultures, knownLabels, uiCulture]
  )

  const changeLanguage = (culture: string | null) => {
    if (culture == null || culture === selectedCulture || changing) return
    setChanging(true)
    setError(false)
    void application.localization
      .setCulture(culture)
      .catch(() => setError(true))
      .finally(() => setChanging(false))
  }

  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      <Label htmlFor={selectId} className="text-xs text-muted-foreground">
        {t(($) => $.language.label)}
      </Label>
      <Select items={items} value={selectedCulture} onValueChange={changeLanguage} disabled={changing}>
        <SelectTrigger id={selectId} className="min-w-32">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <span role="alert" className="text-xs text-destructive">
          {t(($) => $.language.changeFailed)}
        </span>
      )}
    </div>
  )
}

/** A row with a ThemeSwitcher and a LanguageSwitcher, for a header or settings area. Must be inside a ThemeProvider. */
export function PreferenceControls({
  cultures,
  className,
  themeClassName,
  languageClassName,
}: PreferenceControlsProps) {
  return (
    <div className={cn("flex min-w-0 items-center gap-3", className)}>
      <ThemeSwitcher className={themeClassName} />
      <LanguageSwitcher cultures={cultures} className={languageClassName} />
    </div>
  )
}
